import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Copy, ExternalLink, Paperclip, Phone, Plus, Reply, Send, Trash2, TriangleAlert, X } from 'lucide-react';
import {
  CATEGORIAS_TEMPLATE,
  CORPO_AUTENTICACAO,
  LIMITES_TEMPLATE,
  TIPOS_BOTAO_TEMPLATE,
  TIPOS_CABECALHO,
  definicaoDosComponentes,
  preencherVariaveis,
  validarTemplate,
  variaveisDoTexto,
  type BotaoTemplate,
  type CategoriaTemplate,
  type DefinicaoTemplate,
  type TipoCabecalho,
} from '@malgoos/shared';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Alternador, AreaSensivel, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, mensagemDeErro, patch, post, traduzirErro } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { TemplateItem } from './tipos';

/**
 * U14 F4: editor do modelo de mensagem (template oficial), em Comercial > Conversas > Modelos de mensagem. A regra é a do
 * servidor (`validarTemplate`, de `@malgoos/shared`); o rascunho salva com o que tiver, e "Enviar para aprovação" pede tudo.
 */

const CATEGORIA_LOCAL: Record<CategoriaTemplate, TemplateItem['categoria']> = { MARKETING: 'marketing', UTILITY: 'utilidade', AUTHENTICATION: 'autenticacao' };
const CATEGORIA_META: Record<TemplateItem['categoria'], CategoriaTemplate> = { marketing: 'MARKETING', utilidade: 'UTILITY', autenticacao: 'AUTHENTICATION' };
const IDIOMAS = ['pt_BR', 'en_US', 'es'];
const EDITAVEIS = ['APPROVED', 'REJECTED', 'PAUSED'];
const MIME_AMOSTRA = 'image/jpeg,image/png,video/mp4,application/pdf';

const classeArea =
  'w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-base text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-foco disabled:opacity-60 md:text-sm';

/** Situação do modelo na tela: rascunho, enviando, recusado no envio ou a situação da Meta. */
export function situacaoDoTemplate(x: TemplateItem): { chave: string; cor: CorSelo } {
  if (x.envio_estado === 'enviando') return { chave: 'enviando', cor: 'ciano' };
  if (x.envio_estado === 'erro') return { chave: 'erro', cor: 'perigo' };
  const s = (x.situacao_provedor ?? '').toUpperCase();
  if (!s) return x.origem === 'criado' && !x.id_externo ? { chave: 'rascunho', cor: 'neutro' } : { chave: x.status === 'aprovado' ? 'APPROVED' : x.status === 'rejeitado' ? 'REJECTED' : 'PENDING', cor: x.status === 'aprovado' ? 'verde' : x.status === 'rejeitado' ? 'perigo' : 'laranja' };
  if (s === 'FLAGGED') return { chave: s, cor: 'dourado' };
  return { chave: s, cor: x.status === 'aprovado' ? 'verde' : x.status === 'rejeitado' ? 'perigo' : 'laranja' };
}

function vazia(): DefinicaoTemplate {
  return { nome: '', idioma: 'pt_BR', categoria: 'UTILITY', cabecalho: { tipo: 'nenhum' }, corpo: '', exemplos: {}, rodape: '', botoes: [] };
}

function definicaoDoItem(x: TemplateItem): DefinicaoTemplate {
  return definicaoDosComponentes({ nome: x.nome, idioma: x.idioma, categoria: CATEGORIA_META[x.categoria] ?? 'UTILITY', anexo_id: x.amostra_anexo_id }, x.componentes);
}

function Campo({ rotulo, ajuda, erro, children }: { rotulo: string; ajuda?: string; erro?: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <Rotulo id={id} rotulo={rotulo} ajuda={ajuda} erro={erro}>
      {children(id)}
    </Rotulo>
  );
}

const ICONE_BOTAO = { resposta: Reply, link: ExternalLink, telefone: Phone, copiar: Copy } as const;

/** Prévia do modelo como o contato vê (exemplos no lugar das variáveis). */
function Previa({ d }: { d: DefinicaoTemplate }) {
  const { t } = useTranslation('conversas');
  const autenticacao = d.categoria === 'AUTHENTICATION';
  const corpo = autenticacao ? CORPO_AUTENTICACAO.replace('{{1}}', '123456') : preencherVariaveis(d.corpo, d.exemplos);
  const cabecalho = d.cabecalho.tipo === 'texto' ? preencherVariaveis(d.cabecalho.texto ?? '', { [variaveisDoTexto(d.cabecalho.texto ?? '').nomes[0] ?? '']: d.cabecalho.exemplo }) : null;
  return (
    <div>
      <p className="kicker mb-1">{t('modelos.previa')}</p>
      <div className="flex flex-col gap-1 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-1" data-previa-template>
        {d.cabecalho.tipo !== 'nenhum' && d.cabecalho.tipo !== 'texto' && !autenticacao && (
          <p className="flex items-center gap-1.5 text-tinta-3">
            <Paperclip className="size-4" aria-hidden />
            {t(`modelos.tiposCabecalho.${d.cabecalho.tipo}`)}
          </p>
        )}
        {cabecalho && !autenticacao && <p className="font-semibold">{cabecalho}</p>}
        <p className="whitespace-pre-wrap">{corpo || '…'}</p>
        {!autenticacao && d.rodape && <p className="text-xs text-tinta-3">{d.rodape}</p>}
        {d.botoes.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1 border-t border-borda pt-1">
            {d.botoes.map((b, i) => {
              const Icone = ICONE_BOTAO[b.tipo];
              return (
                <li key={i} className="flex items-center justify-center gap-1.5 text-acento-forte">
                  <Icone className="size-4" aria-hidden />
                  {b.tipo === 'copiar' ? b.rotulo || t('modelos.copiarCodigo') : b.rotulo || '…'}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Amostra do cabeçalho de mídia: anexo do próprio modelo (entidade `conversas.template`), pedido pela Meta na aprovação. */
function Amostra({ templateId, anexoId, editavel, aoMudar }: { templateId: string; anexoId: string | null | undefined; editavel: boolean; aoMudar: (id: string | null) => void }) {
  const { t } = useTranslation('conversas');
  const toast = useToast();
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [nome, setNome] = useState<string | null>(null);
  const enviar = async (arquivo: File) => {
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('entidade', 'conversas.template');
      fd.append('registro_id', templateId);
      fd.append('arquivo', arquivo);
      const r = await api<{ id: string }>('POST', '/anexos', fd);
      setNome(arquivo.name.slice(0, 200));
      aoMudar(r.id);
      toast.sucesso(t('modelos.campos.amostraEnviada'));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-tinta-2">{t('modelos.campos.amostra')}</span>
      {anexoId ? (
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <Paperclip className="size-4 shrink-0 text-tinta-3" aria-hidden />
          <span className="min-w-0 flex-1 truncate" title={nome ?? undefined}>
            {nome ?? t('modelos.campos.amostraEnviada')}
          </span>
          {editavel && (
            <Botao variante="fantasma" tamanho="sm" onClick={() => aoMudar(null)}>
              {t('modelos.campos.tirarAmostra')}
            </Botao>
          )}
        </div>
      ) : (
        editavel && (
          <div>
            <input ref={entrada} type="file" accept={MIME_AMOSTRA} className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => e.target.files?.[0] && void enviar(e.target.files[0])} />
            <Botao tamanho="sm" icone={<Paperclip className="size-4" />} carregando={enviando} onClick={() => entrada.current?.click()}>
              {t('modelos.campos.enviarAmostra')}
            </Botao>
          </div>
        )
      )}
      <p className="text-xs text-tinta-3">{t('modelos.campos.ajudaAmostra')}</p>
    </div>
  );
}

function EditorBotoes({ botoes, aoMudar }: { botoes: BotaoTemplate[]; aoMudar: (b: BotaoTemplate[]) => void }) {
  const { t } = useTranslation('conversas');
  const mudar = (i: number, b: Partial<BotaoTemplate>) => aoMudar(botoes.map((x, j) => (j === i ? { ...x, ...b } : x)));
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('modelos.campos.botoes')}</legend>
      <p className="-mt-1 text-xs text-tinta-3">{t('modelos.campos.ajudaBotoes')}</p>
      {botoes.map((b, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-lg border border-borda p-3" data-botao-template={i}>
          <div className="flex items-center gap-2">
            <Selecao
              aria-label={t('modelos.campos.tipoBotao', { n: i + 1 })}
              value={b.tipo}
              onChange={(e) => mudar(i, { tipo: e.target.value as BotaoTemplate['tipo'], url: undefined, telefone: undefined, exemplo: undefined })}
              opcoes={TIPOS_BOTAO_TEMPLATE.map((x) => ({ valor: x, rotulo: t(`modelos.tiposBotao.${x}`) }))}
            />
            <Botao variante="fantasma" tamanho="sm" icone={<X className="size-4" />} aria-label={t('modelos.campos.tirarBotao', { n: i + 1 })} onClick={() => aoMudar(botoes.filter((_, j) => j !== i))} />
          </div>
          {b.tipo !== 'copiar' && <Entrada aria-label={t('modelos.campos.rotulo', { n: i + 1 })} placeholder={t('modelos.campos.rotulo', { n: i + 1 })} value={b.rotulo} maxLength={LIMITES_TEMPLATE.rotulo} onChange={(e) => mudar(i, { rotulo: e.target.value })} />}
          {b.tipo === 'link' && (
            <>
              <Campo rotulo={t('modelos.campos.url', { n: i + 1 })} ajuda={t('modelos.campos.ajudaUrl', { variavel: '{{1}}' })}>
                {(id) => <Entrada id={id} inputMode="url" value={b.url ?? ''} maxLength={LIMITES_TEMPLATE.url} placeholder="https://" onChange={(e) => mudar(i, { url: e.target.value })} />}
              </Campo>
              {(b.url ?? '').includes('{{1}}') && (
                <Campo rotulo={t('modelos.campos.exemploUrl', { n: i + 1 })}>
                  {(id) => <Entrada id={id} value={b.exemplo ?? ''} maxLength={LIMITES_TEMPLATE.exemplo} onChange={(e) => mudar(i, { exemplo: e.target.value })} />}
                </Campo>
              )}
            </>
          )}
          {b.tipo === 'telefone' && (
            <Campo rotulo={t('modelos.campos.telefone', { n: i + 1 })}>
              {(id) => <Entrada id={id} inputMode="tel" value={b.telefone ?? ''} maxLength={25} placeholder="+5511999999999" onChange={(e) => mudar(i, { telefone: e.target.value })} />}
            </Campo>
          )}
          {b.tipo === 'copiar' && (
            <Campo rotulo={t('modelos.campos.exemploCodigo', { n: i + 1 })}>
              {(id) => <Entrada id={id} value={b.exemplo ?? ''} maxLength={LIMITES_TEMPLATE.codigo} onChange={(e) => mudar(i, { exemplo: e.target.value })} />}
            </Campo>
          )}
        </div>
      ))}
      {botoes.length < LIMITES_TEMPLATE.botoes && (
        <div>
          <Botao tamanho="sm" icone={<Plus className="size-4" />} onClick={() => aoMudar([...botoes, { tipo: 'resposta', rotulo: '' }])}>
            {t('modelos.campos.novoBotao')}
          </Botao>
        </div>
      )}
    </fieldset>
  );
}

/** Situação, motivo da Meta, categoria e qualidade do modelo já enviado. */
function Situacao({ x }: { x: TemplateItem }) {
  const { t } = useTranslation('conversas');
  const sit = situacaoDoTemplate(x);
  const cat = (c: string | null) => (c ? t(`templates.categorias.${c}`) : '');
  const motivo = x.motivo_rejeicao ? x.motivo_rejeicao.split(' · ').map((m) => t(`modelos.motivos.${m}`, { defaultValue: m })).join(' · ') : null;
  return (
    <div className="flex flex-col gap-2 text-sm" data-situacao-template={sit.chave}>
      <div className="flex flex-wrap items-center gap-2">
        <Selo cor={sit.cor}>{t(`modelos.situacoes.${sit.chave}`, { defaultValue: sit.chave })}</Selo>
        <Selo>{t(`modelos.origens.${x.origem}`)}</Selo>
        {x.qualidade && (
          <Selo cor={x.qualidade === 'GREEN' ? 'verde' : x.qualidade === 'YELLOW' ? 'dourado' : x.qualidade === 'RED' ? 'perigo' : 'neutro'}>
            {t('modelos.qualidade')}: {t(`modelos.qualidades.${x.qualidade}`, { defaultValue: x.qualidade })}
          </Selo>
        )}
      </div>
      {x.envio_estado === 'erro' && x.envio_erro && (
        <p className="flex items-start gap-2 rounded-lg border border-perigo px-3 py-2 text-perigo">
          <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            {t('modelos.erroEnvio')}: {traduzirErro(x.envio_erro.match(/^([a-z_]+\.[a-z_]+): /)?.[1], x.envio_erro.replace(/^[a-z_]+\.[a-z_]+: /, ''))}
          </span>
        </p>
      )}
      {motivo && x.status !== 'aprovado' && (
        <p className="flex items-start gap-2 rounded-lg border border-perigo px-3 py-2 text-tinta-1">
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-perigo" aria-hidden />
          <span>
            {t('modelos.motivo')}: {motivo}
          </span>
        </p>
      )}
      {x.categoria_prevista && (
        <p className="flex items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-tinta-1">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>{x.categoria_prevista_em ? t('modelos.categoriaPrevistaEm', { nova: cat(x.categoria_prevista), quando: dataHora(x.categoria_prevista_em) }) : t('modelos.categoriaPrevista', { nova: cat(x.categoria_prevista) })}</span>
        </p>
      )}
      {x.categoria_anterior && x.categoria_alterada_em && (
        <p className="flex items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-tinta-1">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>{t('modelos.categoriaMudou', { antes: cat(x.categoria_anterior), agora: cat(x.categoria), quando: dataHora(x.categoria_alterada_em) })}</span>
        </p>
      )}
      {x.enviado_aprovacao_em && x.provedor && <p className="text-xs text-tinta-3">{t('modelos.enviadoEm', { quando: dataHora(x.enviado_aprovacao_em), provedor: t(`modelos.provedores.${x.provedor}`) })}</p>}
    </div>
  );
}

export function EditorTemplate({
  item,
  aberto,
  aoFechar,
  podeEditar,
  podeExcluir,
  conectado,
  aoMudar,
  aoExcluir,
}: {
  /** Nulo: modelo novo. */
  item: TemplateItem | null;
  aberto: boolean;
  aoFechar: () => void;
  podeEditar: boolean;
  podeExcluir: boolean;
  conectado: boolean;
  aoMudar: () => Promise<void>;
  aoExcluir: () => void;
}) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  // Val5 (29/10): enviar à aprovação da Meta (ou Zernio) tem ação própria (`conversas.templates`); editar é `conversas.configurar`.
  const podeAprovacao = useSessao().pode('conversas.templates');
  // O id do modelo novo nasce aqui: a amostra do cabeçalho sobe para ele antes de salvar.
  const [idNovo] = useState(() => crypto.randomUUID());
  const [d, setD] = useState<DefinicaoTemplate>(() => (item ? definicaoDoItem(item) : vazia()));
  const [ocupado, setOcupado] = useState<'salvar' | 'enviar' | null>(null);
  const [tentou, setTentou] = useState(false);
  const idCorpo = useId();
  const id = item?.id ?? idNovo;
  const enviado = !!item?.id_externo;
  const situacao = (item?.situacao_provedor ?? '').toUpperCase();
  const emAnalise = item?.envio_estado === 'enviando' || (enviado && !EDITAVEIS.includes(situacao));
  const editavel = podeEditar && !emAnalise;
  const fixos = enviado;
  const autenticacao = d.categoria === 'AUTHENTICATION';
  const problemas = useMemo(() => validarTemplate(d), [d]);
  const original = useMemo(() => JSON.stringify(item ? definicaoDoItem(item) : vazia()), [item]);
  const mudou = JSON.stringify(d) !== original;
  const varsCorpo = variaveisDoTexto(d.corpo).nomes;
  const erroDe = (campo: string) => (tentou ? problemas.find((p) => p.campo === campo || p.campo.startsWith(`${campo}.`)) : undefined);
  const textoErro = (campo: string) => {
    const p = erroDe(campo);
    return p ? traduzirErro(p.codigo, p.mensagem) : undefined;
  };

  const mudar = (parcial: Partial<DefinicaoTemplate>) => setD((x) => ({ ...x, ...parcial }));
  const mudarCategoria = (categoria: CategoriaTemplate) =>
    setD((x) =>
      categoria === 'AUTHENTICATION'
        ? { ...x, categoria, cabecalho: { tipo: 'nenhum' }, botoes: [{ tipo: 'copiar', rotulo: '', exemplo: 'CODIGO' }], autenticacao: x.autenticacao ?? { seguranca: true, expiracao_minutos: null } }
        : { ...x, categoria, botoes: x.categoria === 'AUTHENTICATION' ? [] : x.botoes },
    );

  const inserirVariavel = () => {
    const nomeada = variaveisDoTexto(d.corpo).formato === 'nomeado';
    const proxima = nomeada ? `{{variavel_${varsCorpo.length + 1}}}` : `{{${varsCorpo.filter((v) => /^\d+$/.test(v)).length + 1}}}`;
    const el = document.getElementById(idCorpo) as HTMLTextAreaElement | null;
    const ini = el?.selectionStart ?? d.corpo.length;
    const fim = el?.selectionEnd ?? d.corpo.length;
    mudar({ corpo: `${d.corpo.slice(0, ini)}${proxima}${d.corpo.slice(fim)}`.slice(0, LIMITES_TEMPLATE.corpo) });
    requestAnimationFrame(() => el?.focus());
  };

  const corpoDaRequisicao = () => ({ ...d, exemplos: Object.fromEntries(varsCorpo.map((v) => [v, d.exemplos[v] ?? ''])) });

  const salvarRascunho = async () => {
    setOcupado('salvar');
    try {
      if (item) await patch(`/conversas/templates/${item.id}`, corpoDaRequisicao());
      else await post('/conversas/templates', { id, ...corpoDaRequisicao() });
      toast.sucesso(t('modelos.salvo'));
      await aoMudar();
      if (!item) aoFechar();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setOcupado(null);
    }
  };

  const enviarAprovacao = async () => {
    setTentou(true);
    if (problemas.length) return;
    setOcupado('enviar');
    try {
      if (!item) await post('/conversas/templates', { id, ...corpoDaRequisicao() });
      // Já enviado e mudado: a edição volta para a aprovação na mesma ação. Rascunho ou reenvio sem mudança: só o envio.
      if (item && enviado && mudou) await patch(`/conversas/templates/${item.id}`, corpoDaRequisicao());
      else {
        if (item && !enviado && mudou) await patch(`/conversas/templates/${item.id}`, corpoDaRequisicao());
        await post(`/conversas/templates/${id}/aprovacao`);
      }
      toast.sucesso(t('modelos.enviado'));
      await aoMudar();
      aoFechar();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
      await aoMudar();
    } finally {
      setOcupado(null);
    }
  };

  // Reenviar sem mudança só vale para rejeitado ou pausado; aprovado sem mudança não precisa.
  const podeEnviar = editavel && conectado && (!enviado || mudou || situacao === 'REJECTED' || situacao === 'PAUSED');

  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      largura="lg"
      titulo={item ? `${item.nome} (${item.idioma})` : t('modelos.titulo')}
      rodape={
        <>
          {item && podeExcluir && (
            <Botao variante="perigo" className="mr-auto" icone={<Trash2 className="size-4" />} onClick={aoExcluir}>
              {t('templates.excluir')}
            </Botao>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {editavel ? tn('comum.cancelar') : tn('comum.fechar')}
          </Botao>
          {editavel && !enviado && (
            <Botao disabled={!!ocupado || (!mudou && !!item)} carregando={ocupado === 'salvar'} onClick={() => void salvarRascunho()}>
              {t('modelos.salvarRascunho')}
            </Botao>
          )}
          {editavel && podeAprovacao && (
            <Botao variante="primario" icone={<Send className="size-4" />} disabled={!!ocupado || !podeEnviar} title={conectado ? undefined : t('acoes.conectorOff')} carregando={ocupado === 'enviar'} onClick={() => void enviarAprovacao()}>
              {enviado && !mudou ? t('modelos.reenviar') : t('modelos.enviarAprovacao')}
            </Botao>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4" data-editor-template={item?.id ?? 'novo'}>
        {item && (item.origem === 'criado' || enviado) && <Situacao x={item} />}
        {emAnalise && <p className="text-sm text-tinta-3">{t('modelos.somenteLeitura')}</p>}
        {!podeEditar && <p className="text-sm text-tinta-3">{t('modelos.semPermissao')}</p>}
        {tentou && problemas.length > 0 && (
          <div role="alert" className="flex flex-col gap-1 rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            <p className="font-medium">{t('modelos.problemas')}</p>
            <ul className="flex flex-col gap-1">
              {[...new Set(problemas.map((p) => traduzirErro(p.codigo, p.mensagem)))].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <fieldset disabled={!editavel} className="flex min-w-0 flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Campo rotulo={t('modelos.campos.nome')} ajuda={t('modelos.campos.ajudaNome')} erro={textoErro('nome')}>
              {(cid) => <Entrada id={cid} className="font-mono" value={d.nome} disabled={fixos} maxLength={LIMITES_TEMPLATE.nome} autoComplete="off" spellCheck={false} onChange={(e) => mudar({ nome: e.target.value.toLowerCase().replace(/\s+/g, '_') })} />}
            </Campo>
            <Campo rotulo={t('modelos.campos.idioma')} erro={textoErro('idioma')}>
              {(cid) => <Selecao id={cid} value={d.idioma} disabled={fixos} onChange={(e) => mudar({ idioma: e.target.value })} opcoes={[...new Set([...IDIOMAS, d.idioma])].map((x) => ({ valor: x, rotulo: x }))} />}
            </Campo>
            <Campo rotulo={t('modelos.campos.categoria')} erro={textoErro('categoria')}>
              {(cid) => (
                <Selecao id={cid} value={d.categoria} disabled={fixos} onChange={(e) => mudarCategoria(e.target.value as CategoriaTemplate)} opcoes={CATEGORIAS_TEMPLATE.map((c) => ({ valor: c, rotulo: t(`templates.categorias.${CATEGORIA_LOCAL[c]}`) }))} />
              )}
            </Campo>
          </div>

          {autenticacao ? (
            <>
              <p className="text-sm text-tinta-3">{t('modelos.campos.ajudaAutenticacao')}</p>
              <label className="flex items-center gap-3 text-sm text-tinta-2">
                <Alternador marcado={!!d.autenticacao?.seguranca} rotulo={t('modelos.campos.seguranca')} aoMudar={(v) => mudar({ autenticacao: { seguranca: v, expiracao_minutos: d.autenticacao?.expiracao_minutos ?? null } })} />
                {t('modelos.campos.seguranca')}
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rotulo={t('modelos.campos.expiracao')} ajuda={t('modelos.campos.ajudaExpiracao')} erro={textoErro('autenticacao')}>
                  {(cid) => (
                    <Entrada
                      id={cid}
                      type="number"
                      min={1}
                      max={LIMITES_TEMPLATE.expiracao}
                      value={d.autenticacao?.expiracao_minutos ?? ''}
                      onChange={(e) => mudar({ autenticacao: { seguranca: !!d.autenticacao?.seguranca, expiracao_minutos: e.target.value === '' ? null : Number(e.target.value) } })}
                    />
                  )}
                </Campo>
                <Campo rotulo={t('modelos.campos.rotuloCopiar')} erro={textoErro('botoes')}>
                  {(cid) => <Entrada id={cid} value={d.botoes[0]?.rotulo ?? ''} maxLength={LIMITES_TEMPLATE.rotulo} onChange={(e) => mudar({ botoes: [{ tipo: 'copiar', rotulo: e.target.value, exemplo: 'CODIGO' }] })} />}
                </Campo>
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <Campo rotulo={t('modelos.campos.tipoCabecalho')}>
                  {(cid) => (
                    <Selecao
                      id={cid}
                      value={d.cabecalho.tipo}
                      onChange={(e) => mudar({ cabecalho: { tipo: e.target.value as TipoCabecalho } })}
                      opcoes={TIPOS_CABECALHO.map((x) => ({ valor: x, rotulo: t(`modelos.tiposCabecalho.${x}`) }))}
                    />
                  )}
                </Campo>
                {d.cabecalho.tipo === 'texto' && (
                  <div className="sm:col-span-2">
                    <Campo rotulo={t('modelos.campos.textoCabecalho')} ajuda={t('modelos.campos.ajudaCabecalho')} erro={textoErro('cabecalho')}>
                      {(cid) => <Entrada id={cid} value={d.cabecalho.texto ?? ''} maxLength={LIMITES_TEMPLATE.cabecalho} onChange={(e) => mudar({ cabecalho: { ...d.cabecalho, texto: e.target.value } })} />}
                    </Campo>
                  </div>
                )}
              </div>
              {d.cabecalho.tipo === 'texto' && variaveisDoTexto(d.cabecalho.texto ?? '').nomes[0] && (
                <Campo rotulo={t('modelos.campos.exemplo', { variavel: `{{${variaveisDoTexto(d.cabecalho.texto ?? '').nomes[0]}}}` })}>
                  {(cid) => <Entrada id={cid} value={d.cabecalho.exemplo ?? ''} maxLength={LIMITES_TEMPLATE.exemplo} onChange={(e) => mudar({ cabecalho: { ...d.cabecalho, exemplo: e.target.value } })} />}
                </Campo>
              )}
              {d.cabecalho.tipo !== 'nenhum' && d.cabecalho.tipo !== 'texto' && (
                <>
                  <Amostra templateId={id} anexoId={d.cabecalho.anexo_id} editavel={editavel} aoMudar={(anexo_id) => mudar({ cabecalho: { ...d.cabecalho, anexo_id } })} />
                  {textoErro('cabecalho') && <p className="text-xs text-perigo">{textoErro('cabecalho')}</p>}
                </>
              )}

              <Rotulo id={idCorpo} rotulo={t('modelos.campos.corpo')} ajuda={t('modelos.campos.ajudaCorpo', { numero: '{{1}}', nome: '{{nome}}' })} erro={textoErro('corpo')}>
                <div className="flex flex-col gap-1.5">
                  <AreaSensivel id={idCorpo} rows={6} maxLength={LIMITES_TEMPLATE.corpo} value={d.corpo} onChange={(e) => mudar({ corpo: e.target.value })} className={classeArea} />
                  <div className="flex items-center justify-between gap-2">
                    <Botao tamanho="sm" icone={<Plus className="size-4" />} onClick={inserirVariavel}>
                      {t('modelos.campos.inserirVariavel')}
                    </Botao>
                    <span className="text-xs text-tinta-3 tabular-nums">
                      {d.corpo.length}/{LIMITES_TEMPLATE.corpo}
                    </span>
                  </div>
                </div>
              </Rotulo>

              {varsCorpo.length > 0 && (
                <fieldset className="flex flex-col gap-3">
                  <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('modelos.campos.exemplos')}</legend>
                  <p className="-mt-1 text-xs text-tinta-3">{t('modelos.campos.ajudaExemplos')}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {varsCorpo.map((v) => (
                      <Campo key={v} rotulo={t('modelos.campos.exemplo', { variavel: `{{${v}}}` })} erro={textoErro(`exemplos.${v}`)}>
                        {(cid) => <Entrada id={cid} value={d.exemplos[v] ?? ''} maxLength={LIMITES_TEMPLATE.exemplo} onChange={(e) => mudar({ exemplos: { ...d.exemplos, [v]: e.target.value } })} />}
                      </Campo>
                    ))}
                  </div>
                </fieldset>
              )}

              <Campo rotulo={t('modelos.campos.rodape')} ajuda={t('modelos.campos.ajudaRodape')} erro={textoErro('rodape')}>
                {(cid) => <Entrada id={cid} value={d.rodape} maxLength={LIMITES_TEMPLATE.rodape} onChange={(e) => mudar({ rodape: e.target.value })} />}
              </Campo>

              <EditorBotoes botoes={d.botoes} aoMudar={(botoes) => mudar({ botoes })} />
              {textoErro('botoes') && <p className="text-xs text-perigo">{textoErro('botoes')}</p>}
            </>
          )}
        </fieldset>
        <Previa d={d} />
      </div>
    </Drawer>
  );
}
